import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import Footer from '@/components/Footer';
import { API } from '@/App';

const DynamicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      const response = await axios.get(`${API}/pages/${slug}`);
      setPage(response.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Page non trouvée' : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const renderBlock = (block) => {
    switch (block.type) {
      case 'hero':
        return (
          <section key={block.id} className="pt-32 pb-16 px-4 gradient-bg">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {block.content.title}
              </h1>
              {block.content.subtitle && (
                <p className="text-xl text-gray-700 leading-relaxed">
                  {block.content.subtitle}
                </p>
              )}
            </div>
          </section>
        );

      case 'text':
        return (
          <section key={block.id} className="py-8 px-4">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {block.content.content}
                </p>
              </div>
            </div>
          </section>
        );

      case 'image':
        return (
          <section key={block.id} className="py-8 px-4">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src={block.content.src}
                  alt={block.content.alt || ''}
                  className="w-full h-auto"
                />
                {block.content.caption && (
                  <div className="bg-gray-50 p-4 text-center">
                    <p className="text-sm text-gray-600">{block.content.caption}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        );

      case 'list':
        return (
          <section key={block.id} className="py-8 px-4">
            <div className="max-w-4xl mx-auto">
              {block.content.title && (
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {block.content.title}
                </h2>
              )}
              <ul className="space-y-3">
                {(block.content.items || []).map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-block w-2 h-2 bg-cyan-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        );

      case 'cards':
        return (
          <section key={block.id} className="py-16 px-4 bg-gray-50">
            <div className="max-w-6xl mx-auto">
              {block.content.title && (
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                  {block.content.title}
                </h2>
              )}
              <div className="grid md:grid-cols-3 gap-8">
                {(block.content.cards || []).map((card, index) => (
                  <Card key={index}>
                    <CardContent className="p-6 text-center">
                      {card.icon && (
                        <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <span className="text-2xl">{card.icon}</span>
                        </div>
                      )}
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        {card.title}
                      </h3>
                      <p className="text-gray-600">
                        {card.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#1e3a5f] text-lg">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{error}</h1>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  if (!page || !page.published) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Page non disponible</h1>
          <p className="text-gray-600 mb-6">Cette page n'est pas encore publiée</p>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img src="/SFOALC-logo.png" alt="SFOALC" className="h-12" />
            </div>
            <Button onClick={() => navigate(-1)} variant="outline" className="flex items-center gap-2">
              <ArrowLeft size={18} />
              Retour
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-16">
        {page.content_blocks.map(block => renderBlock(block))}
      </div>

      <Footer />
    </div>
  );
};

export default DynamicPage;
